import type { GoogleDriveFile, GoogleDriveAuthResult } from '../types/googleDrive';

export class GoogleDriveService {
  private static instance: GoogleDriveService;
  private isInitialized = false;
  private isSignedIn = false;

  // Google API設定
  private readonly CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  private readonly API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;
  private readonly DISCOVERY_DOC = 'https://www.googleapis.com/discovery/v1/apis/drive/v3/rest';
  private readonly SCOPES = 'https://www.googleapis.com/auth/drive.readonly';

  // MIMEタイプ定数
  static readonly MIME_TYPES = {
    FOLDER: 'application/vnd.google-apps.folder',
    DOCUMENT: 'application/vnd.google-apps.document',
    SPREADSHEET: 'application/vnd.google-apps.spreadsheet',
    PRESENTATION: 'application/vnd.google-apps.presentation',
    FORM: 'application/vnd.google-apps.form',
    PDF: 'application/pdf',
    IMAGE: ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'],
    VIDEO: ['video/mp4', 'video/quicktime', 'video/x-msvideo'],
    TEXT: ['text/plain', 'text/csv', 'text/html'],
  } as const;

  private constructor() {}

  static getInstance(): GoogleDriveService {
    if (!GoogleDriveService.instance) {
      GoogleDriveService.instance = new GoogleDriveService();
    }
    return GoogleDriveService.instance;
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    if (!this.CLIENT_ID || !this.API_KEY) {
      throw new Error('Google API credentials not configured');
    }

    return new Promise((resolve, reject) => {
      // Google API スクリプトを並行して読み込み
      const loadPromises: Promise<void>[] = [];

      // gapi の読み込み
      if (typeof window.gapi === 'undefined') {
        loadPromises.push(this.loadScript('https://apis.google.com/js/api.js'));
      }

      // Google Identity Services の読み込み
      if (typeof window.google === 'undefined') {
        loadPromises.push(this.loadScript('https://accounts.google.com/gsi/client'));
      }

      Promise.all(loadPromises)
        .then(() => this.initializeGapi())
        .then(resolve)
        .catch(reject);
    });
  }

  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }

  private async initializeGapi(): Promise<void> {
    // gapiライブラリを読み込み
    await new Promise<void>((resolve) => {
      window.gapi.load('client', resolve);
    });

    // clientを初期化
    await window.gapi.client.init({
      apiKey: this.API_KEY,
      discoveryDocs: [this.DISCOVERY_DOC]
    });

    this.isInitialized = true;
  }

  async signIn(): Promise<GoogleDriveAuthResult> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      // Google Identity Services を使用
      const tokenResponse = await new Promise<any>((resolve, reject) => {
        if (typeof window.google !== 'undefined' && 
            window.google.accounts && 
            window.google.accounts.oauth2) {
          const tokenClient = window.google.accounts.oauth2.initTokenClient({
            client_id: this.CLIENT_ID!,
            scope: this.SCOPES,
            callback: (response: any) => {
              if (response.error) {
                reject(new Error(response.error));
              } else {
                resolve(response);
              }
            },
          });
          tokenClient.requestAccessToken();
        } else {
          reject(new Error('Google Identity Services not loaded'));
        }
      });

      // アクセストークンを設定
      window.gapi.client.setToken({
        access_token: tokenResponse.access_token
      });

      this.isSignedIn = true;

      return {
        success: true,
        user: {
          id: 'user-id',
          name: 'User Name',
          email: 'user@example.com',
          imageUrl: ''
        }
      };
    } catch (error) {
      console.error('Google Sign-In failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  async signOut(): Promise<void> {
    if (!this.isInitialized) return;

    if (typeof window.gapi !== 'undefined' && window.gapi.client.getToken()) {
      window.gapi.client.setToken({ access_token: '' });
    }
    this.isSignedIn = false;
  }

  isAuthenticated(): boolean {
    return this.isSignedIn && 
           typeof window.gapi !== 'undefined' && 
           !!window.gapi.client.getToken();
  }

  async listFolders(parentId?: string): Promise<GoogleDriveFile[]> {
    if (!this.isAuthenticated()) {
      throw new Error('Not authenticated');
    }

    try {
      const query = parentId 
        ? `'${parentId}' in parents and mimeType='application/vnd.google-apps.folder' and trashed=false`
        : `mimeType='application/vnd.google-apps.folder' and trashed=false`;

      const response: any = await window.gapi.client.drive.files.list({
        q: query,
        fields: 'files(id,name,mimeType,parents,webViewLink)',
        orderBy: 'name'
      });

      return response.result.files.map((file: any) => ({
        id: file.id,
        name: file.name,
        mimeType: file.mimeType,
        webViewLink: file.webViewLink,
        parents: file.parents
      }));
    } catch (error) {
      console.error('Failed to list folders:', error);
      throw new Error('Failed to fetch folders from Google Drive');
    }
  }

  async listFilesInFolder(folderId: string): Promise<GoogleDriveFile[]> {
    if (!this.isAuthenticated()) {
      throw new Error('Not authenticated');
    }

    try {
      const response: any = await window.gapi.client.drive.files.list({
        q: `'${folderId}' in parents and trashed=false`,
        fields: 'files(id,name,mimeType,parents,webViewLink,size,modifiedTime)',
        orderBy: 'name'
      });

      return response.result.files.map((file: any) => ({
        id: file.id,
        name: file.name,
        mimeType: file.mimeType,
        webViewLink: file.webViewLink,
        parents: file.parents,
        size: file.size,
        modifiedTime: file.modifiedTime
      }));
    } catch (error) {
      console.error('Failed to list files:', error);
      throw new Error('Failed to fetch files from Google Drive');
    }
  }

  async searchFiles(query: string, options?: {
    folderId?: string;
    mimeType?: string;
    pageSize?: number;
    pageToken?: string;
  }): Promise<{files: GoogleDriveFile[], nextPageToken?: string}> {
    if (!this.isAuthenticated()) {
      throw new Error('Not authenticated');
    }

    try {
      // 検索クエリの構築
      let searchQuery = `fullText contains '${query}' and trashed=false`;
      
      if (options?.folderId) {
        searchQuery += ` and '${options.folderId}' in parents`;
      }
      
      if (options?.mimeType) {
        searchQuery += ` and mimeType='${options.mimeType}'`;
      }

      const response: any = await window.gapi.client.drive.files.list({
        q: searchQuery,
        fields: 'files(id,name,mimeType,parents,webViewLink,size,modifiedTime),nextPageToken',
        pageSize: options?.pageSize || 50,
        pageToken: options?.pageToken,
        orderBy: 'modifiedTime desc'
      });

      return {
        files: response.result.files.map((file: any) => ({
          id: file.id,
          name: file.name,
          mimeType: file.mimeType,
          webViewLink: file.webViewLink,
          parents: file.parents,
          size: file.size,
          modifiedTime: file.modifiedTime
        })),
        nextPageToken: response.result.nextPageToken
      };
    } catch (error) {
      console.error('Failed to search files:', error);
      throw new Error('Failed to search files in Google Drive');
    }
  }

  async getFolderBreadcrumb(folderId: string): Promise<GoogleDriveFile[]> {
    if (!this.isAuthenticated()) {
      throw new Error('Not authenticated');
    }

    const breadcrumb: GoogleDriveFile[] = [];
    let currentId: string | null = folderId;

    try {
      while (currentId) {
        const response: any = await window.gapi.client.drive.files.get({
          fileId: currentId,
          fields: 'id,name,parents,webViewLink'
        });

        const folder: any = response.result;
        breadcrumb.unshift({
          id: folder.id,
          name: folder.name,
          mimeType: 'application/vnd.google-apps.folder',
          webViewLink: folder.webViewLink,
          parents: folder.parents
        });

        currentId = folder.parents && folder.parents.length > 0 ? folder.parents[0] : null;
      }

      return breadcrumb;
    } catch (error) {
      console.error('Failed to get folder breadcrumb:', error);
      return [];
    }
  }

  getViewableLink(file: GoogleDriveFile): string {
    // Google Driveファイルを直接プレビューできるリンクを生成
    if (file.mimeType === 'application/vnd.google-apps.folder') {
      return file.webViewLink;
    }

    // ドキュメント系ファイルの場合は直接表示用リンクを生成
    if (file.mimeType?.startsWith('application/vnd.google-apps.')) {
      return file.webViewLink;
    }

    // その他のファイルの場合は埋め込み表示用リンクを試す
    return `https://drive.google.com/file/d/${file.id}/preview`;
  }

  formatFileSize(bytes?: string): string {
    if (!bytes) return '-';
    
    const size = parseInt(bytes);
    if (size === 0) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    
    return parseFloat((size / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  formatModifiedTime(modifiedTime?: string): string {
    if (!modifiedTime) return '-';
    
    const date = new Date(modifiedTime);
    return date.toLocaleDateString('ja-JP') + ' ' + date.toLocaleTimeString('ja-JP', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }

  getFileIconClass(mimeType?: string): string {
    if (!mimeType) return 'icon-file';
    
    if (mimeType === GoogleDriveService.MIME_TYPES.FOLDER) return 'icon-folder';
    if (mimeType === GoogleDriveService.MIME_TYPES.DOCUMENT) return 'icon-document';
    if (mimeType === GoogleDriveService.MIME_TYPES.SPREADSHEET) return 'icon-spreadsheet';
    if (mimeType === GoogleDriveService.MIME_TYPES.PRESENTATION) return 'icon-presentation';
    if (mimeType === GoogleDriveService.MIME_TYPES.FORM) return 'icon-form';
    if (mimeType === GoogleDriveService.MIME_TYPES.PDF) return 'icon-pdf';
    
    if (GoogleDriveService.MIME_TYPES.IMAGE.includes(mimeType as any)) return 'icon-image';
    if (GoogleDriveService.MIME_TYPES.VIDEO.includes(mimeType as any)) return 'icon-video';
    if (GoogleDriveService.MIME_TYPES.TEXT.includes(mimeType as any)) return 'icon-text';
    
    if (mimeType.startsWith('audio/')) return 'icon-audio';
    if (mimeType.startsWith('application/zip') || mimeType.startsWith('application/x-')) return 'icon-archive';
    
    return 'icon-file';
  }

  async downloadFile(fileId: string): Promise<string> {
    if (!this.isAuthenticated()) {
      throw new Error('Not authenticated');
    }

    try {
      const response: any = await window.gapi.client.drive.files.get({
        fileId: fileId,
        alt: 'media'
      });

      return response.body;
    } catch (error) {
      console.error('Failed to download file:', error);
      throw new Error('Failed to download file from Google Drive');
    }
  }
}
